import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Slide,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import { Icon } from "@iconify/react";
import WelcomeStep from "./steps/WelcomeStep";
import SelectSourceType from "./steps/SelectSourceType";
import UploadAndParse from "./steps/UploadAndParse";
import DetectOptionalData from "./steps/DetectOptionalData";
import MapDataFields from "./steps/MapDataFields";
import ReviewAndClean from "./steps/ReviewAndClean";
import SiteMappingStep from "./steps/SiteMappingStep";
import DryRunValidation from "./steps/DryRunValidation";
import ImportResults from "./steps/ImportResults";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const steps = [
  { label: "Willkommen", description: "Einführung in den Import-Prozess" },
  { label: "Quelle wählen", description: "Datenformat auswählen" },
  { label: "Datei hochladen", description: "Datei hochladen und analysieren" },
  { label: "Daten erkennen", description: "Optionale Daten erkennen" },
  { label: "Felder zuordnen", description: "Spalten zu Feldern zuordnen" },
  { label: "Überprüfen", description: "Daten überprüfen und bearbeiten" },
  { label: "Standort zuordnen", description: "Mitglieder Standorten zuordnen" },
  { label: "Validierung", description: "Trockenlauf-Validierung" },
  { label: "Fertig", description: "Import abgeschlossen" },
];

function DataImportWizard({ handleClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [importData, setImportData] = useState({
    sourceType: null, // 'csv' | 'json' | 'manual'
    file: null,
    parsedData: null,
    detectedTypes: {
      hasContracts: false,
      hasEvents: false,
      hasBookings: false,
      hasSiteManagers: false,
    },
    fieldMapping: null,
    cleanedData: null,
    validationResults: null,
    importResults: null,
  });

  const handleNext = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleUpdateData = useCallback((updates) => {
    setImportData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setImportData({
      sourceType: null,
      file: null,
      parsedData: null,
      detectedTypes: {
        hasContracts: false,
        hasEvents: false,
        hasBookings: false,
        hasSiteManagers: false,
      },
      fieldMapping: null,
      cleanedData: null,
      validationResults: null,
      importResults: null,
    });
  }, []);

  const handleFinish = useCallback(() => {
    handleReset();
    handleClose("REFETCH");
  }, [handleClose, handleReset]);

  const handleSelectSourceType = useCallback(
    (sourceType) => {
      handleUpdateData({ sourceType });
      handleNext();
    },
    [handleNext, handleUpdateData],
  );

  const handleParsed = useCallback(
    (parsedData, file) => {
      handleUpdateData({ parsedData, file });
      handleNext();
    },
    [handleNext, handleUpdateData],
  );

  const handleDetected = useCallback(
    (detectedTypes) => {
      handleUpdateData({ detectedTypes });
      handleNext();
    },
    [handleNext, handleUpdateData],
  );

  const handleMapped = useCallback(
    (fieldMapping) => {
      handleUpdateData({ fieldMapping });
      handleNext();
    },
    [handleNext, handleUpdateData],
  );

  const handleCleaned = useCallback(
    (cleanedData) => {
      handleUpdateData({ cleanedData });
      handleNext();
    },
    [handleNext, handleUpdateData],
  );

  const handleValidated = useCallback(
    (validationResults) => {
      handleUpdateData({ validationResults });
    },
    [handleUpdateData],
  );

  const handleExecuted = useCallback(
    (importResults) => {
      handleUpdateData({ importResults });
      handleNext();
    },
    [handleNext, handleUpdateData],
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} onSkip={handleClose} />;
      case 1:
        return (
          <SelectSourceType
            selectedType={importData.sourceType}
            onSelect={handleSelectSourceType}
          />
        );
      case 2:
        return (
          <UploadAndParse
            sourceType={importData.sourceType}
            onParsed={handleParsed}
          />
        );
      case 3:
        return (
          <DetectOptionalData
            parsedData={importData.parsedData}
            sourceType={importData.sourceType}
            onDetected={handleDetected}
          />
        );
      case 4:
        return (
          <MapDataFields
            parsedData={importData.parsedData}
            sourceType={importData.sourceType}
            detectedTypes={importData.detectedTypes}
            onMapped={handleMapped}
          />
        );
      case 5:
        return (
          <ReviewAndClean
            parsedData={importData.parsedData}
            fieldMapping={importData.fieldMapping}
            onCleaned={handleCleaned}
          />
        );
      case 6:
        return (
          <SiteMappingStep
            cleanedData={importData.cleanedData}
            parsedData={importData.parsedData}
            onMapped={(mappedData) => {
              handleUpdateData({ cleanedData: mappedData });
              handleNext();
            }}
          />
        );
      case 7:
        return (
          <DryRunValidation
            cleanedData={importData.cleanedData}
            onValidated={handleValidated}
            onExecute={handleExecuted}
          />
        );
      case 8:
        return (
          <ImportResults
            results={importData.importResults}
            onFinish={handleFinish}
          />
        );
      default:
        return <Typography>Unbekannter Schritt</Typography>;
    }
  };

  const canGoBack = activeStep > 0 && activeStep < steps.length - 1;
  const canClose = activeStep === 0 || activeStep === steps.length - 1;

  return (
    <Dialog
      open
      TransitionComponent={Transition}
      keepMounted
      maxWidth="xl"
      fullWidth
      aria-describedby="data-import-wizard"
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogActions
        sx={{
          borderBottom: 2,
          borderColor: "divider",
          px: 3,
          py: 2,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Icon icon="mdi:database-import" width={32} color="primary" />
          <Typography variant="h4">Daten importieren</Typography>
        </Box>
        <Button onClick={canClose ? handleClose : null} color="inherit">
          <Icon width="40" icon="icon-park-solid:close-one" />
        </Button>
      </DialogActions>

      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="body2" fontWeight={activeStep === index ? "bold" : "normal"}>
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 2,
            minHeight: "400px",
            bgcolor: "background.default",
          }}
        >
          {renderStepContent()}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, borderTop: 2, borderColor: "divider" }}>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={handleBack}
            disabled={!canGoBack}
            startIcon={<Icon icon="mdi:arrow-left" />}
          >
            Zurück
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
            Schritt {activeStep + 1} von {steps.length}
          </Typography>
          <Box sx={{ width: 100 }} /> {/* Spacer for balance */}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

DataImportWizard.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

export default DataImportWizard;
